import {Col, Form, Row} from "react-bootstrap";
import {ADMIN_LANG} from "../../../../settings";
import {useAppDispatch, useAppSelector} from "../../../../app/store";
import {changeQuestionModal} from "../../../../app/slices/modals/questionEditModal";
import {QuestionScaleOption} from "../../../../types/admin";
import React from "react";

type Props = {
    language: string;
    is_draft: boolean;
};

const QuestionEditScale = ({language, is_draft}: Props) => {
    const {min_scale, max_scale, number_scale_options, rating_type, content_id} = useAppSelector((state) => state.questionEditModal);
    const dispatch = useAppDispatch();
    const npsScale = rating_type === 'nps';

    const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newMin = parseInt(e.target.value);
        const adjustedMin = Math.max(newMin, -10);

        dispatch(changeQuestionModal({ min_scale: adjustedMin }));

        const maxAllowedMax = adjustedMin + 10;
        if (max_scale > maxAllowedMax) {
            dispatch(changeQuestionModal({ max_scale: maxAllowedMax }));
        }
    };

    const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newMax = parseInt(e.target.value);
        const adjustedMax = Math.min(newMax, 10);

        dispatch(changeQuestionModal({ max_scale: adjustedMax }));

        const minAllowedMin = adjustedMax - 10;
        if (min_scale < minAllowedMin) {
            dispatch(changeQuestionModal({ min_scale: minAllowedMin }));
        }
    };

    const handleRatingTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newRatingType = e.target.checked ? 'nps' : 'default';
        dispatch(changeQuestionModal({ rating_type: newRatingType }));
    };

    return (
        <Col>
            {!content_id && (
            <Form.Group className='mb-3' controlId='rating_type'>
            <Form.Switch
                className='form-label'
                checked={npsScale}
                onChange={handleRatingTypeChange}
                label={ADMIN_LANG.nps_scale}
                />
            </Form.Group>
            )}
            {number_scale_options.length < 1 && !npsScale && (
                <Row className="mb-3">
                <Col xs={6}>
                    <Form.Group controlId='minScale'>
                        <Form.Label>{ADMIN_LANG.min_value}</Form.Label>
                        <Form.Control
                            type='number'
                            name='max_scale'
                            value={min_scale}
                            onChange={handleMinChange}
                            min="-10"
                            max="9"
                            disabled={!is_draft}
                        />
                    </Form.Group>
                </Col>
                <Col xs={6}>
                    <Form.Group controlId='maxScale'>
                        <Form.Label>{ADMIN_LANG.max_value}</Form.Label>
                        <Form.Control
                            type='number'
                            name='max_scale'
                            value={max_scale}
                            onChange={handleMaxChange}
                            min="1"
                            max="10"
                            disabled={!is_draft}
                        />
                    </Form.Group>
                </Col>
            </Row>
            )}



            {number_scale_options.length > 0 && (
            <>
            <Row className='mb-2'>
                <Col xs={2}>
                    <Form.Label className="mb-0">
                        {ADMIN_LANG.rating}
                    </Form.Label>
                </Col>
                <Col>
                    <Form.Label className="mb-0">
                        {ADMIN_LANG.label}
                    </Form.Label>
                </Col>
            </Row>
                {number_scale_options.map((option, index) => {
                    const isEditable = shouldOptionBeEditable(index, number_scale_options.length);
                    if (!isEditable) return null;
                    return (
                        <ScaleOption
                            key={option.key}
                            option={option}
                            language={language}
                            readonly={false}
                            isEditable={true}
                        />
                    );
                })}
                </>
            )}
        </Col>
    );
}

export default QuestionEditScale;


const shouldOptionBeEditable = (index: number, totalOptions: number): boolean => {
    if (totalOptions <= 5) {
        return true;
    }
    if (index === 0 || index === totalOptions - 1) {
        return true;
    }
    const centerIndex = Math.floor(totalOptions / 2);
    return totalOptions % 2 !== 0 && index === centerIndex;
};

export const ScaleOption = ({option, language, readonly, isEditable}: {
    option: QuestionScaleOption,
    language: string,
    readonly: boolean,
    isEditable: boolean
}) => {
    const dispatch = useAppDispatch();
    const { number_scale_options } = useAppSelector((state) => state.questionEditModal);

    const languageOption = option.languages.find(
        (lang) => lang.language_code === language
    ) || { language_code: language, option: '' };

    const handleOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const title = e.target.value;
        const updatedOptions = number_scale_options.map((scaleOption) => {
            if (scaleOption.option_id === option.option_id) {
                return {
                    ...scaleOption,
                    languages: scaleOption.languages.map((languageOption) =>
                        languageOption.language_code === language
                            ? { ...languageOption, option: title }
                            : languageOption
                    ),
                };
            }
            return scaleOption;
        });

        dispatch(changeQuestionModal({ number_scale_options: updatedOptions }));
    };

    return (
        <Row className="mb-2 align-items-center">
            <Col xs={2}>
                <Form.Control
                    type="number"
                    value={option.rating}
                    readOnly
                    disabled
                />
            </Col>
            <Col>
                <Form.Control
                    type="text"
                    value={languageOption.option}
                    onChange={handleOptionChange}
                    disabled={readonly || !isEditable}
                />
            </Col>
        </Row>
    );
};